import { Close, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { DataGrid } from "@mui/x-data-grid";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import get from "lodash/get";
import { default as Moment } from 'moment';
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import Breadcrumb from "../components/Breadcrumb";
import MainLayout from "../components/MainLayout";
import ProtectedPermissionPage from "../components/ProtectedPermissionPage";
import branch from "../services/branch";
import sales from "../services/sales";
import config from "../utils/config";
import { rupiah } from "../utils/currency";
import queryDefault from "../utils/queryDefault";
import ReportTable from "./report/ReportTable";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

const chartSetting = {
  height: 300,
};

function capitalizeFLetter(input: string) {
  return input[0].toUpperCase() + input.slice(1);
}

function replaceAll(str: string, find: string, replace: string) {
  return str.replace(new RegExp(find, 'g'), replace);
}

export default function Summary() {
  const [pageSize, setPageSize] = useState<number>(10);
  const [valueFrom, setValueFrom] = useState<Dayjs | null>(dayjs(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [valueTo, setValueTo] = useState<Dayjs | null>(dayjs(new Date()));
  const [countTotalKertas, setCountTotalKertas] = useState(0);
  const [countFreeVoucher, setCountFreeVoucher] = useState(0);
  const [countCrewTeman, setCountCrewTeman] = useState(0);
  const [countFraud, setCountFraud] = useState(0);
  const [countDoublePrint, setCountDoublePrint] = useState(0);
  const [countPrintError, setCountPrintError] = useState(0);
  const [countLainnya, setCountLainnya] = useState(0);
  const [countTotalTransaksi, setCountTotalTransaksi] = useState(0);
  const [countSales, setCountSales] = useState(0);
  const [options, setOptions] = useState<any>({
    keyword: "",
  });
  const listBranch = useQuery(
    ["branch", options],
    ({ queryKey }) => {
      return branch.getAllBranch({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const onHandleSearch = useCallback((values: { keyword: any }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);

  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });

  const [options2, setOptions2] = useState<any>({
    branch_ids: [],
    startDate: Moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('YYYY-MM-DD'),
    endDate: Moment(new Date()).format('YYYY-MM-DD'),
  });

  const form2 = useForm({
    defaultValues: options2,
  });

  const [countStats, setCountStats] = useState([
    {
      grouped_date: "",
      total_kertas: 0,
      free_voucher: 0,
      crew_teman: 0,
      total_kesalahan_fraud: 0,
      double_print: 0,
      print_error_test: 0,
      lainnya: 0,
      total_transaksi_dashboard: 0,
    },
  ]);

  const [salesStats, setSalesStats] = useState([
    { grouped_date: "", subtotal: 0 },
  ]);

  const listBranchStats = useQuery(
    ["branch-stats", options2],
    ({ queryKey }) => {
      return branch.getBranchesStatsRange({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  useEffect(() => {
    if (listBranchStats.data?.data?.count?.length > 0) {
      var countStats: any = [];
      var countTotalKertasTemp = 0;
      var countFreeVoucherTemp = 0;
      var countCrewTemanTemp = 0;
      var countFraudTemp = 0;
      var countDoublePrintTemp = 0;
      var countPrintErrorTemp = 0;
      var countLainnyaTemp = 0;
      var countTotalTransaksiTemp = 0;
      for (const i of listBranchStats.data.data.count) {
        countStats.push({
          grouped_date: `${i.grouped_date.split('-')[2]}-${i.grouped_date.split('-')[1]}`,
          total_kertas: Number(i.total_kertas),
          free_voucher: Number(i.free_voucher),
          crew_teman: Number(i.crew_teman),
          total_kesalahan_fraud: Number(i.total_kesalahan_fraud),
          double_print: Number(i.double_print),
          print_error_test: Number(i.print_error_test),
          lainnya: Number(i.lainnya),
          total_transaksi_dashboard: Number(i.total_transaksi_dashboard),
        });
        countTotalKertasTemp = countTotalKertasTemp + Number(i.total_kertas);
        countFreeVoucherTemp = countFreeVoucherTemp + Number(i.free_voucher);
        countCrewTemanTemp = countCrewTemanTemp + Number(i.crew_teman);
        countFraudTemp = countFraudTemp + Number(i.total_kesalahan_fraud);
        countDoublePrintTemp = countDoublePrintTemp + Number(i.double_print);
        countPrintErrorTemp = countPrintErrorTemp + Number(i.print_error_test);
        countLainnyaTemp = countLainnyaTemp + Number(i.lainnya);
        countTotalTransaksiTemp = countTotalTransaksiTemp + Number(i.total_transaksi_dashboard);
      }
      setCountStats(countStats);
      setCountTotalKertas(countTotalKertasTemp);
      setCountFreeVoucher(countFreeVoucherTemp);
      setCountCrewTeman(countCrewTemanTemp);
      setCountFraud(countFraudTemp);
      setCountDoublePrint(countDoublePrintTemp);
      setCountPrintError(countPrintErrorTemp);
      setCountLainnya(countLainnyaTemp);
      setCountTotalTransaksi(countTotalTransaksiTemp);
    } else {
      setCountStats([]);
      setCountTotalKertas(0);
      setCountFreeVoucher(0);
      setCountCrewTeman(0);
      setCountFraud(0);
      setCountDoublePrint(0);
      setCountPrintError(0);
      setCountLainnya(0);
      setCountTotalTransaksi(0);
    }
    if (listBranchStats.data?.data?.sales?.length > 0) {
      var salesStats: any = [];
      var countSalesTemp = 0;
      for (const i of listBranchStats.data.data.sales) {
        salesStats.push({
          grouped_date: `${i.grouped_date.split('-')[2]}-${i.grouped_date.split('-')[1]}`,
          subtotal: Number(i.subtotal)
        });
        countSalesTemp = countSalesTemp + Number(i.subtotal);
      }
      setSalesStats(salesStats);
      setCountSales(countSalesTemp)
    } else {
      setSalesStats([]);
      setCountSales(0);
    }
  }, [listBranchStats]);

  useEffect(() => {
    form2.setValue("startDate", Moment(valueFrom?.format('YYYY-MM-DD')).format('YYYY-MM-DD'));
    form2.setValue("endDate", Moment(valueTo?.format('YYYY-MM-DD')).format('YYYY-MM-DD'));
    setOptions2({
      ...options2,
      startDate: form2.getValues().startDate,
      endDate: form2.getValues().endDate,
      branch_ids: form2.getValues().branch_ids,
    });
  }, [valueFrom, valueTo, form2]);

  const listSalesPerformance = useQuery(
    ["listSalesPerformance", options2],
    ({ queryKey }) => {
      return sales.salesPerformanceRange({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  return (
    <MainLayout>
      <Helmet>
        <title>Summary</title>
      </Helmet>

      <Breadcrumb
        label="Summary"
        breadcrumbs={[{ label: "Branch", href: "/app/branch" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2, 10]}>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <Controller
              name="keyword"
              control={form.control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Input Keyword here"
                  {...field}
                  {...config.onEnter(() => {
                    form.handleSubmit(onHandleSearch)();
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() => {
                          if (field.value) {
                            field.onChange("");
                            setOptions((prev: any) => ({
                              ...prev,
                              keyword: "",
                            }));
                          }
                        }}
                      >
                        {field.value ? (
                          <Close color="error" sx={{ cursor: "pointer" }} />
                        ) : (
                          <Search />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              variant="outlined"
              onClick={form.handleSubmit(onHandleSearch)}
            >
              SEARCH
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                const allBranchIds = get(listBranch, "data.data", []).map((branch: any) => branch.id);
                form2.setValue("branch_ids", allBranchIds);
                setOptions2({
                  ...options2,
                  branch_ids: allBranchIds,
                });
              }}>
              SELECT ALL
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                form2.setValue("branch_ids", []);
                setOptions2({
                  ...options2,
                  branch_ids: [],
                });
              }}>
              UNSELECT ALL
            </Button>
          </Stack>
        </Box>
        <Box sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1}>
            <FormControl margin="normal">
              <FormLabel>Start Date</FormLabel>
              <Controller
                name="date_from"
                control={form2.control}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      inputFormat="DD/MM/YYYY"
                      views={["day", "month", "year"]}
                      value={valueFrom}
                      onChange={newValue => {
                        setValueFrom(newValue);
                      }}
                      renderInput={props => <TextField {...props} />}
                    />
                  </LocalizationProvider>
                )}
              />
            </FormControl>

            <FormControl margin="normal">
              <FormLabel>End Date</FormLabel>
              <Controller
                name="date_to"
                control={form2.control}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      inputFormat="DD/MM/YYYY"
                      views={["day", "month", "year"]}
                      value={valueTo}
                      onChange={newValue => {
                        setValueTo(newValue);
                      }}
                      renderInput={props => <TextField {...props} />}
                    />
                  </LocalizationProvider>
                )}
              />
            </FormControl>
          </Stack>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6">
            Selected Branches: {form2.getValues("branch_ids")?.map((id: number) => get(listBranch, "data.data", []).find((branch: any) => branch.id === id)?.name).join(", ") || "None"}
          </Typography>
        </Box>
        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listBranch.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={get(listBranch, "data.data", [])}
            rowCount={get(listBranch, "data.data", []).length}
            page={options.page_number}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30, 50, 100]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "group_name",
                headerName: "Group",
                flex: 1,
                filterable: false,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "name",
                headerName: "Name",
                flex: 1,
                filterable: false,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "place",
                headerName: "Place",
                flex: 1,
                filterable: false,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography >{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "actions",
                headerName: "Action",
                sortable: false,
                filterable: false,
                width: 200,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          var currentBranches = form2.getValues("branch_ids") || [];
                          if (!currentBranches.includes(get(params, "row.id"))) {
                            currentBranches.push(get(params, "row.id"));
                          } else {
                            currentBranches.splice(currentBranches.indexOf(get(params, "row.id")), 1);
                          }
                          form2.setValue("branch_ids", currentBranches);
                          setOptions2({
                            ...options2,
                            branch_ids: currentBranches,
                          });
                        }}
                      >
                        {form2.getValues("branch_ids").includes(get(params, "row.id")) ? "Unselect" : "Select"}
                      </Button>
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h2">Stats</Typography>
          <Typography variant="h3" sx={{ mt: 2 }}>Total Kertas ({countTotalKertas})</Typography>
          <Typography variant="h3" >Total Free Voucher ({countFreeVoucher})</Typography>
          <Typography variant="h3" >Total Crew Teman ({countCrewTeman})</Typography>
          <Typography variant="h3" >Total Fraud ({countFraud})</Typography>
          <BarChart
            sx={{ "& .MuiChartsAxis-tickLabel tspan": { fontSize: "10px" } }}
            dataset={countStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "grouped_date",
                label: "Date-Month",
              },
            ]}
            series={[
              { dataKey: "total_kertas", label: "Total" },
              { dataKey: "total_free_voucher", label: "Total Free Voucher" },
              { dataKey: "crew_teman", label: "Total Crew Teman" },
              { dataKey: "total_kesalahan_fraud", label: "Total Fraud" },
            ]}
            {...chartSetting}
          />
          <Typography variant="h3" sx={{ mt: 2 }}>Total Double Print ({countDoublePrint})</Typography>
          <Typography variant="h3" >Total Print Error ({countPrintError})</Typography>
          <Typography variant="h3" >Total Lainnya ({countLainnya})</Typography>
          <Typography variant="h3" >Total Transaksi ({countTotalTransaksi})</Typography>
          <BarChart
            sx={{ "& .MuiChartsAxis-tickLabel tspan": { fontSize: "10px" } }}
            dataset={countStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "grouped_date",
                label: "Date-Month",
              },
            ]}
            series={[
              { dataKey: "double_print", label: "Total Double Print" },
              { dataKey: "print_error_test", label: "Total Print Error" },
              { dataKey: "lainnya", label: "Total Lainnya" },
              { dataKey: "total_transaksi_dashboard", label: "Total Transaksi" }
            ]}
            {...chartSetting}
          />
          <Typography variant="h3" sx={{ mt: 2 }}>Revenue ({rupiah(countSales)})</Typography>
          <BarChart
            sx={{ "& .MuiChartsAxis-tickLabel tspan": { fontSize: "10px" } }}
            dataset={salesStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "grouped_date",
                label: "Date-Month",
              },
            ]}
            series={[{ dataKey: "subtotal", label: "Total" }]}
            {...chartSetting}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h2">Sales Performance</Typography>
          <ReportTable
            head={listSalesPerformance.isLoading ? [<th className="greenBackground" key="name">Name</th>]
              : Object.keys(get(listSalesPerformance, "data.data", ["name"])[0])
                .map((data: any) => {
                  return <th className="greenBackground" key={data}>
                    {capitalizeFLetter(replaceAll(data, "_", " "))}
                  </th>
                })}
            body={listSalesPerformance.isLoading ? <tr><td>Loading, please wait</td></tr> : get(listSalesPerformance, "data.data", []).map((data: any) => (
              <tr>
                {
                  Object.keys(get(listSalesPerformance, "data.data", ["name"])[0]).map((v: any) => {
                    if (v === "omzet" || v === "subtotal") {
                      return <td>{rupiah(data[v])}</td>
                    } else {
                      return <td>{data[v]}</td>
                    }
                  })
                }
              </tr>
            )
            )}>
          </ReportTable>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
